import React from 'react';

import { withTailwindWrapper } from './withTailwindWrapper';

// Discriminated union: tw=true requires T props, tw=false/undefined requires F props
type CombinedProps<T, F> = (T & { tw: true }) | (F & { tw?: false });

export function withStyleComponentFallback<T extends object, F extends object>(
  TwComponent: React.ComponentType<T>,
  FallbackComponent: React.ComponentType<F>
) {
  // Create wrapped component once when factory is called (not during render)
  const TwWrappedComponent = withTailwindWrapper(TwComponent);

  // Create the main component function
  const CombinedComponent = (props: CombinedProps<T, F>) => {
    // If tw prop is present, use the Tailwind component
    if (props.tw) {
      return <TwWrappedComponent {...(props as T)} />;
    }

    // Otherwise use the fallback component
    return <FallbackComponent {...(props as F)} />;
  };

  // Create a proper wrapper that preserves subcomponents
  const WrapperComponent = Object.assign(CombinedComponent, {
    // Copy all properties from FallbackComponent
    ...FallbackComponent,
    // Also copy properties from TwComponent (but don't override FallbackComponent properties)
    ...Object.fromEntries(
      Object.entries(TwComponent).filter(([key]) => !(key in FallbackComponent))
    )
  });

  return WrapperComponent;
}

// Alternative export name for clarity
export const withTwFallback = withStyleComponentFallback;
